import React from 'react';
import {
  Box, Button, Tooltip, IconButton, CircularProgress, Alert, TableContainer, Paper, Table, TableHead, TableRow, TableCell, TableBody, Dialog, DialogTitle, DialogContent, DialogActions, TextField, FormControl, InputLabel, Select, MenuItem, Tabs, Tab, Typography, List, ListItem, ListItemText, Divider
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import AddIcon from '@mui/icons-material/Add';
import WalletRow from '../components/WalletRow';
import AddTransactionModal from '../AddTransactionModal';
import { useWalletsPage } from '../hooks/useWalletsPage';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import SyncProgress from '../components/SyncProgress';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useLocation } from 'react-router-dom';
import { useLanguageNavigation } from '../hooks/useLanguageNavigation';

function WalletsPage() {
  const { t } = useTranslation();
  const {
    loading,
    error,
    syncError,
    syncProgress,
    handleManualRefresh,
    setSnackbar,
    handleOpenColumnsDialog,
    isRTL,
    visibleColumns,
    allColumns,
    sortColumn,
    sortDirection,
    handleSort,
    sortedWallets,
    handleDeleteClick,
    columnRenderers,
    prices,
    formatCurrency,
    handleRefreshWallet,
    setOpen,
    open,
    modalType,
    setModalType,
    editingTransaction,
    newTransaction,
    modalError,
    blockchains,
    blockchainsLoading,
    newWallet,
    setNewWallet,
    handleClose,
    handleSubmit,
    coinsLoading,
    handleAddManualHolding,
    deleteConfirmOpen,
    handleDeleteCancel,
    handleDeleteConfirm,
    clearSyncProgress,
    columnsDialogOpen,
    handleCloseColumnsDialog,
    handleSaveColumnsDialog,
    tempVisibleColumns,
    tempHiddenColumns,
    handleMoveColumnUp,
    handleMoveColumnDown,
    handleHideColumn,
    handleShowColumn
  } = useWalletsPage();
  const { navigateWithLang } = useLanguageNavigation();
  const location = useLocation();

  return (
    <Box sx={{ p: { xs: 1, sm: 3 } }}>
      <Typography variant="h4" component="h1" sx={{ fontWeight: 700, mb: 2 }}>
        {t('navigation.wallets')}
      </Typography>
      {/* Sync Progress */}
      <SyncProgress syncProgress={syncProgress} onClose={clearSyncProgress} />
      {/* Action Buttons Row: Add Holdings, Refresh All, Columns */}
      <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: isRTL ? 'flex-start' : 'flex-end', alignItems: 'center', mb: 1, gap: 1 }}>
        <Button
          variant="outlined"
          size="small"
          onClick={() => navigateWithLang('add-holdings')}
          sx={{
            borderRadius: '999px',
            fontWeight: 400,
            fontSize: '0.95rem',
            px: 1.5,
            py: 0.1,
            minWidth: 0,
            height: 32,
            color: '#1976d2',
            borderColor: '#1976d2',
            boxShadow: 'none',
            textTransform: 'none',
            letterSpacing: 0,
            '&:hover': { bgcolor: '#e6f0fa', borderColor: '#1976d2' },
          }}
        >
          {t('wallets.addHoldings')}
        </Button>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={handleManualRefresh}
          disabled={loading}
          size="small"
          sx={{
            borderRadius: '999px',
            fontWeight: 400,
            fontSize: '0.95rem',
            px: 1.5,
            py: 0.1,
            minWidth: 0,
            height: 32,
            color: '#1976d2',
            borderColor: '#1976d2',
            boxShadow: 'none',
            textTransform: 'none',
            letterSpacing: 0,
            '&:hover': { bgcolor: '#e6f0fa', borderColor: '#1976d2' },
          }}
        >
          {t('wallets.refreshAll')}
        </Button>
        <Tooltip title={t('common.columns')}>
          <IconButton onClick={handleOpenColumnsDialog} size="large">
            <ViewColumnIcon />
          </IconButton>
        </Tooltip>
      </Box>
      {loading ? (
        <Box display="flex" justifyContent="center" p={3}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="error">{error}</Alert>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow
                sx={isRTL ? { direction: 'rtl' } : {}}
              >
                <TableCell></TableCell>
                {(visibleColumns || []).map((colId) => {
                  const column = allColumns.find(c => c.id === colId);
                  return (
                    <TableCell
                      key={colId}
                      onClick={() => handleSort(colId)}
                      style={{ cursor: 'pointer' }}
                      align={isRTL ? 'right' : (column?.headerAlign || column?.align || 'left')}
                      sx={{
                        textAlign: isRTL ? 'right' : undefined,
                        '& .MuiBox-root': {
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.5,
                          flexDirection: isRTL ? 'row-reverse' : 'row'
                        }
                      }}
                    >
                      <Box sx={isRTL ? { justifyContent: 'flex-end', textAlign: 'right', width: '100%', whiteSpace: 'nowrap', minWidth: 110 } : { whiteSpace: 'nowrap', minWidth: 110 }}>
                        {column.label}
                        {sortColumn === colId && (
                          <Box component="span" sx={{ display: 'inline-flex', verticalAlign: 'middle' }}>
                            {sortDirection === 'asc' ? <ArrowDropUpIcon fontSize="small" /> : <ArrowDropDownIcon fontSize="small" />}
                          </Box>
                        )}
                      </Box>
                    </TableCell>
                  );
                })}
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedWallets.map((wallet) => (
                <WalletRow
                  key={wallet.id}
                  wallet={wallet}
                  onDelete={() => handleDeleteClick(wallet.id)}
                  visibleColumns={visibleColumns}
                  columnRenderers={columnRenderers}
                  allColumns={allColumns}
                  hasError={!!syncError && syncProgress?.walletId === wallet.id}
                  errorMessage={syncError && syncProgress?.walletId === wallet.id ? syncError : ''}
                  handleRefreshWallet={() => {
                    handleRefreshWallet(wallet.id);
                  }}
                  currentPrices={prices}
                  formatCurrency={formatCurrency}
                  isRTL={isRTL}
                />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      
      {/* Add Holdings Modal */}
      <Dialog open={open} onClose={() => handleClose(true)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingTransaction ? t('portfolio.editHoldings') : t('portfolio.addHoldings')}</DialogTitle>
        <Tabs
          value={modalType}
          onChange={(_, newValue) => setModalType(newValue)}
          variant="fullWidth"
          indicatorColor="primary"
          textColor="primary"
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab label={t('wallets.addWallet')} value="wallet" />
          <Tab label={editingTransaction ? t('wallets.editManualTransaction') : t('wallets.addManualTransaction')} value="transaction" />
        </Tabs>
        <Box sx={{ p: { xs: 1, sm: 3 } }}>
          {modalType === 'wallet' ? (
            <>
              {modalError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {modalError}
                </Alert>
              )}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
                <FormControl fullWidth required error={Boolean(modalError && !newWallet.blockchain)}>
                  <InputLabel>{t('common.blockchain')}</InputLabel>
                  <Select
                    value={newWallet.blockchain}
                    label={t('common.blockchain')}
                    onChange={(e) => setNewWallet(prev => ({ ...prev, blockchain: e.target.value }))}
                    disabled={blockchainsLoading}
                  >
                    {blockchains.map((chain, index) => (
                      <MenuItem 
                        key={chain.symbol || `blockchain-${index}`} 
                        value={(chain.symbol || '').toLowerCase()}
                        disabled={!chain.is_supported}
                        sx={{
                          opacity: chain.is_supported ? 1 : 0.5,
                          color: chain.is_supported ? 'inherit' : 'text.disabled'
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <img 
                            src={chain.icon_url} 
                            alt={chain.name} 
                            style={{ 
                              width: 24, 
                              height: 24,
                              opacity: chain.is_supported ? 1 : 0.5
                            }} 
                          />
                          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                            <span>{chain.name}</span>
                            {!chain.is_supported && (
                              <span style={{ 
                                fontSize: '0.75rem', 
                                color: 'text.disabled',
                                fontStyle: 'italic'
                              }}>
                                Coming Soon
                              </span>
                            )}
                          </Box>
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField
                  label={t('common.address')}
                  value={newWallet.address}
                  onChange={(e) => setNewWallet(prev => ({ ...prev, address: e.target.value }))}
                  fullWidth
                  required
                  error={Boolean(modalError && !newWallet.address)}
                  helperText={modalError && !newWallet.address ? t('common.enterAddress') : ''}
                />
                <TextField
                  label={t('common.walletLabel')}
                  value={newWallet.label}
                  onChange={(e) => setNewWallet(prev => ({ ...prev, label: e.target.value }))}
                  fullWidth
                />
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 3 }}>
                <Button onClick={() => handleClose(true)} color="secondary" variant="outlined" sx={{ minWidth: 100 }}>
                  {t('common.cancel')}
                </Button>
                <Button onClick={handleSubmit} variant="contained" sx={{ minWidth: 100 }}>
                  {t('common.addWallet')}
                </Button>
              </Box>
            </>
          ) : (
            <AddTransactionModal
              formOnly={true}
              onClose={() => handleClose(true)}
              onSubmit={handleAddManualHolding}
              loading={coinsLoading}
              initialValues={newTransaction}
              error={modalError}
              submitButtonText={editingTransaction ? t('wallets.updateTransaction') : t('wallets.addTransaction')}
              editing={!!editingTransaction}
            />
          )}
        </Box>
      </Dialog>
      {/* Wallet Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={handleDeleteCancel}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">
          {t('dialogs.deleteWallet.title')}
        </DialogTitle>
        <DialogContent>
          <Typography id="delete-dialog-description">
            {t('dialogs.deleteWallet.message')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} color="primary">
            {t('common.cancel')}
          </Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained" autoFocus>
            {t('dialogs.deleteWallet.confirmButton')}
          </Button>
        </DialogActions>
      </Dialog>
      {/* Wallet Columns Dialog */}
      <Dialog open={columnsDialogOpen} onClose={handleCloseColumnsDialog} maxWidth="xs" fullWidth>
        <DialogTitle>{t('common.columns')}</DialogTitle>
        <DialogContent>
          <List>
            {tempVisibleColumns.map((colId, index) => {
              const col = allColumns.find(c => c.id === colId);
              return (
                <ListItem
                  key={colId + '-' + index}
                  style={{ display: 'flex', alignItems: 'center' }}
                  secondaryAction={
                    <>
                      <IconButton edge="end" onClick={() => handleMoveColumnUp(index)} disabled={index === 0}>
                        <ArrowUpwardIcon />
                      </IconButton>
                      <IconButton edge="end" onClick={() => handleMoveColumnDown(index)} disabled={index === tempVisibleColumns.length - 1}>
                        <ArrowDownwardIcon />
                      </IconButton>
                      <IconButton edge="end" onClick={() => handleHideColumn(colId)} disabled={tempVisibleColumns.length === 1}>
                        <VisibilityOffIcon />
                      </IconButton>
                    </>
                  }
                >
                  <ListItemText primary={t(`tables.wallets.${colId}`) || col?.label || colId} />
                </ListItem>
              );
            })}
          </List>
          <Divider sx={{ my: 1 }} />
          <List>
            {tempHiddenColumns.map((colId, index) => {
              const col = allColumns.find(c => c.id === colId);
              return (
                <ListItem key={colId + '-' + index} secondaryAction={
                  <IconButton edge="end" onClick={() => handleShowColumn(colId)}>
                    <VisibilityIcon />
                  </IconButton>
                }>
                  <ListItemText primary={t(`tables.wallets.${colId}`) || col?.label || colId} />
                </ListItem>
              );
            })}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseColumnsDialog}>{t('common.cancel')}</Button>
          <Button onClick={handleSaveColumnsDialog} variant="contained" autoFocus>{t('common.saveChanges')}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default WalletsPage; 